/**
 * Observer processor that tracks tool outputs for denormalization.
 *
 * This is a stateful processor that maintains a map of tool outputs by reference ID (for dataRef resolution)
 *
 * Other processors (like WidgetTransformProcessor) can use these maps
 * to resolve dataRef references to actual data.
 *
 * This processor doesn't transform parts - it only observes and records.
 * It should be first in any chain where widget transformation needs
 * to resolve references.
 *
 * Used in pipelines:
 * - model-to-client: Track outputs for widget denormalization
 */

import type { MessagePart, ToolResponse } from '#schemas/message-schemas.js';
import type { RecommendationType } from '#schemas/widget-schemas.js';
import type { ProcessorContext } from '../types.js';
import type { PartResult } from './PartProcessor.js';

import { isToolResponsePart } from '#schemas/message-schemas.js';

import { PartProcessor } from './PartProcessor.js';
import { collectRecommendationEntities } from './recommendation-entity-index.js';

export class ToolOutputTrackerProcessor extends PartProcessor {
  readonly name = 'ToolOutputTracker';
  readonly #outputsByRef = new Map<string, unknown>();
  /** Every output of each tool this turn, in call order — the symbolic ref keeps only the latest. */
  readonly #outputsByName = new Map<string, unknown[]>();
  readonly #recommendationTypes = new Map<string, RecommendationType>();

  static toSymbolicDataRef(toolName: string): string {
    return `@dataRef-from-${toolName}`;
  }

  /**
   * Get the tracked tool outputs by ref map.
   * Used by other processors (via context) to resolve dataRef references.
   */
  get toolOutputs(): ReadonlyMap<string, unknown> {
    return this.#outputsByRef;
  }

  /**
   * Get tool output by reference ID.
   */
  getByRef(ref: string): unknown | undefined {
    return this.#outputsByRef.get(ref);
  }

  /**
   * All outputs a tool produced this turn, oldest first. A widget that lifts facts by entity id
   * reads these rather than the symbolic ref, so two searches in one turn each feed their own
   * carousel instead of the later one overwriting the earlier.
   */
  getAllByName(toolName: string): readonly unknown[] {
    return this.#outputsByName.get(toolName) ?? [];
  }

  /** Recommendation type for an entity id a data tool returned this conversation, if any. */
  getRecommendationType(entityId: string): RecommendationType | undefined {
    return this.#recommendationTypes.get(entityId);
  }

  trackToolOutput(toolResponse: ToolResponse): void {
    if (toolResponse.output === undefined) return;

    if (toolResponse.ref) {
      this.#outputsByRef.set(toolResponse.ref, toolResponse.output);
    }

    if (toolResponse.name) {
      this.#outputsByRef.set(ToolOutputTrackerProcessor.toSymbolicDataRef(toolResponse.name), toolResponse.output);
      this.#outputsByName.set(toolResponse.name, [...this.getAllByName(toolResponse.name), toolResponse.output]);
      for (const [id, type] of collectRecommendationEntities(toolResponse.name, toolResponse.output)) {
        this.#recommendationTypes.set(id, type);
      }
    }
  }

  process(part: MessagePart, _context: ProcessorContext): PartResult {
    if (isToolResponsePart(part)) {
      this.trackToolOutput(part.toolResponse);
    }
    // Always pass through unchanged - this is an observer only
    return { parts: [part] };
  }

  /**
   * Reset the tracked outputs.
   * Should be called at the start of a new chat session (not between turns).
   */
  reset(): void {
    this.#outputsByRef.clear();
    this.#outputsByName.clear();
    this.#recommendationTypes.clear();
  }
}
